import { ref, watch, onUnmounted, getCurrentInstance } from 'vue';

/** Duración de la animación (coincide con `adminBellRing`: 0,5 s). */
export const RING_DURATION_MS = 600;

/** Ventana para que una ráfaga de notificaciones suene una sola vez. */
export const RING_DEBOUNCE_MS = 1500;

/**
 * Balanceo de la campana al llegar notificaciones nuevas.
 *
 * Reglas:
 * - No suena si el panel de notificaciones está abierto: el usuario ya está
 *   viendo la lista.
 * - Una sola vez por ráfaga (debounce).
 * - El tick (`nuevasNotificaciones`) solo sube con notificaciones realmente
 *   nuevas, nunca en la carga inicial ni al marcar como leídas.
 *
 * @param {import('vue').Ref<boolean>} open panel de notificaciones abierto
 * @param {import('vue').Ref<number>} tick contador de notificaciones nuevas
 * @returns {{ringing: import('vue').Ref<boolean>}}
 */
export function useNotificationBellRing(open, tick) {
    const ringing = ref(false);
    let timer = null;
    // -Infinity: el primer aviso suena siempre, sin que el debounce lo coma.
    let lastRingAt = -Infinity;

    function ring() {
        const now = Date.now();
        if (now - lastRingAt < RING_DEBOUNCE_MS) return;

        lastRingAt = now;
        ringing.value = true;
        clearTimeout(timer);
        timer = setTimeout(() => {
            ringing.value = false;
        }, RING_DURATION_MS);
    }

    watch(tick, () => {
        if (open.value) return;
        ring();
    });

    if (getCurrentInstance()) {
        onUnmounted(() => {
            clearTimeout(timer);
        });
    }

    return { ringing };
}
